import type { ApolloLink } from '@apollo/client';
import {
  getDirectiveValues,
  getOperationAST,
  GraphQLError,
  GraphQLIncludeDirective,
  GraphQLSkipDirective,
  isAbstractType,
  isEnumType,
  isInputObjectType,
  isInputType,
  isInterfaceType,
  isLeafType,
  isListType,
  isNonNullType,
  isObjectType,
  isScalarType,
  Kind,
  typeFromAST,
  TypeInfo,
  valueFromASTUntyped,
  visit,
  visitWithTypeInfo,
} from 'graphql';
import type {
  DirectiveNode,
  FieldNode,
  GraphQLCompositeType,
  GraphQLInputType,
  GraphQLLeafType,
  GraphQLOutputType,
  GraphQLSchema,
  SelectionSetNode,
} from 'graphql';
import { isLosslessNumber } from 'lossless-json';
import type { LosslessNumber } from 'lossless-json';

type ConvertResponseArgs = Pick<ResponsePlanArgs, 'schema'> &
  (
    | {
        /**
         * Applies the application's BigDecimal policy after native JSON parsing.
         */
        decimal: DecimalPolicy;
        mode: 'domain';
      }
    | { mode: 'wire' }
  ) & {
    /**
     * Contains either lossless wire tokens or native transport values.
     */
    data: Record<string, unknown>;
    /**
     * Retains the declared output type of each selected response field.
     */
    plan: ResponsePlan;
  };
type DecimalPolicy = (value: unknown) => unknown;

type DirectedNode = { readonly directives?: readonly DirectiveNode[] };
type FieldPlan = { readonly key: string; readonly value: ResponsePlan };
type ResponsePlan =
  | { readonly declared: GraphQLCompositeType; readonly fields: readonly FieldPlan[]; readonly kind: 'object' }
  | { readonly item: ResponsePlan; readonly kind: 'list' }
  | { readonly kind: 'leaf'; readonly type: GraphQLLeafType }
  | { readonly kind: 'unresolved' };
type ResponsePlanArgs = {
  /**
   * Supplies source selections and directive variables for this operation.
   */
  operation: ApolloLink.Operation;
  /**
   * Supplies the client's known field contracts without adding backend runtime types.
   */
  schema: GraphQLSchema;
};
type SerializeVariablesArgs = ResponsePlanArgs & {
  /**
   * Serializes finite BigDecimal inputs without converting them to native numbers.
   */
  decimal: DecimalPolicy;
};

export const compileResponsePlan = ({ operation, schema }: ResponsePlanArgs): ResponsePlan => {
  const definition = getOperationAST(operation.query, operation.operationName);
  if (!definition) {
    throw new GraphQLError('GraphQL operation is required');
  }

  const variables = { ...operation.variables };
  const variableDefinitions = definition.variableDefinitions ?? [];
  for (const variable of variableDefinitions) {
    if (variables[variable.variable.name.value] === undefined && variable.defaultValue) {
      variables[variable.variable.name.value] = valueFromASTUntyped(variable.defaultValue);
    }
  }
  const active = (node: DirectedNode) =>
    getDirectiveValues(GraphQLSkipDirective, node, variables)?.if !== true &&
    getDirectiveValues(GraphQLIncludeDirective, node, variables)?.if !== false;
  const fields = new WeakMap<FieldNode, GraphQLOutputType | null>();
  const info = new TypeInfo(schema);
  visit(
    operation.query,
    visitWithTypeInfo(info, {
      Field(node) {
        fields.set(node, info.getType() ?? null);
      },
    })
  );
  const fragments = new Map(
    operation.query.definitions.flatMap(node =>
      node.kind === Kind.FRAGMENT_DEFINITION ? [[node.name.value, node] as const] : []
    )
  );
  const compileSelections = (selectionSet: SelectionSetNode | undefined, ancestors: ReadonlySet<string>): FieldPlan[] =>
    selectionSet?.selections.flatMap(node => {
      if (!active(node)) {
        return [];
      }
      if (node.kind === Kind.FIELD) {
        return {
          key: node.alias?.value ?? node.name.value,
          value: compileType(fields.get(node) ?? null, node.selectionSet, ancestors),
        };
      }
      if (node.kind === Kind.INLINE_FRAGMENT) {
        return compileSelections(node.selectionSet, ancestors);
      }
      if (ancestors.has(node.name.value)) {
        throw new GraphQLError('Cyclic GraphQL fragment');
      }
      const fragment = fragments.get(node.name.value);

      return fragment ? compileSelections(fragment.selectionSet, new Set([...ancestors, node.name.value])) : [];
    }) ?? [];
  const compileType = (
    type: GraphQLOutputType | null,
    selections: SelectionSetNode | undefined,
    ancestors: ReadonlySet<string>
  ): ResponsePlan => {
    if (!type) {
      return { kind: 'unresolved' };
    }
    if (isNonNullType(type)) {
      return compileType(type.ofType, selections, ancestors);
    }
    if (isListType(type)) {
      return { item: compileType(type.ofType, selections, ancestors), kind: 'list' };
    }
    if (isLeafType(type)) {
      return { kind: 'leaf', type };
    }

    return { declared: type, fields: compileSelections(selections, ancestors), kind: 'object' };
  };
  const root =
    definition.operation === 'query'
      ? schema.getQueryType()
      : definition.operation === 'mutation'
        ? schema.getMutationType()
        : schema.getSubscriptionType();

  return root ? compileType(root, definition.selectionSet, new Set()) : { kind: 'unresolved' };
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) && !isLosslessNumber(value);

const visitNumbers = (value: unknown, visitNumber: (value: LosslessNumber) => void): void => {
  if (isLosslessNumber(value)) {
    visitNumber(value);
  } else if (typeof value === 'object' && value !== null) {
    for (const item of Object.values(value)) {
      visitNumbers(item, visitNumber);
    }
  }
};

export const convertResponse = (args: ConvertResponseArgs): Record<string, unknown> => {
  const { data, mode, plan, schema } = args;
  const classified = new WeakSet<LosslessNumber>();
  const convertLeaf = (value: unknown, type: GraphQLLeafType, candidates: readonly ResponsePlan[]): unknown => {
    if (candidates.some(candidate => candidate.kind === 'leaf' && candidate.type.name !== type.name)) {
      throw new Error('Unclassified numeric GraphQL response scalar conflict');
    }
    if (type.name === 'BigDecimal') {
      return args.mode === 'wire' ? (isLosslessNumber(value) ? value.value : value) : args.decimal(value);
    }
    if (mode === 'wire') {
      if (isScalarType(type)) {
        visitNumbers(value, token => classified.add(token));
      }

      return value;
    }

    return isScalarType(type) ? type.parseValue(value) : value;
  };
  const collectFields = (value: Record<string, unknown>, candidates: readonly ResponsePlan[]) => {
    const applicable = candidates.flatMap(candidate => {
      if (candidate.kind !== 'object') {
        return [];
      }
      if (!isAbstractType(candidate.declared)) {
        return [candidate];
      }
      const typename = value.__typename;
      if (typeof typename !== 'string' || !typename) {
        return [];
      }
      const runtime = schema.getType(typename);

      return isObjectType(runtime) || (!runtime && isInterfaceType(candidate.declared)) ? [candidate] : [];
    });
    const byKey = new Map<string, ResponsePlan[]>();
    for (const candidate of applicable) {
      for (const field of candidate.fields) {
        if (Object.hasOwn(value, field.key)) {
          byKey.set(field.key, [...(byKey.get(field.key) ?? []), field.value]);
        }
      }
    }

    return byKey;
  };
  const convert = (value: unknown, candidates: readonly ResponsePlan[]): unknown => {
    if (value == null) {
      return value;
    }
    const known = candidates.filter(candidate => candidate.kind !== 'unresolved');
    const first = known[0];
    if (!first) {
      return value;
    }
    if (known.some(candidate => candidate.kind !== first.kind)) {
      throw new Error('Unclassified numeric GraphQL response type conflict');
    }
    if (first.kind === 'leaf') {
      return convertLeaf(value, first.type, known);
    }
    if (first.kind === 'list') {
      const items = known.flatMap(candidate => (candidate.kind === 'list' ? [candidate.item] : []));

      return Array.isArray(value) ? value.map(item => convert(item, items)) : value;
    }
    if (!isObject(value)) {
      throw new TypeError('GraphQL response object has an invalid shape');
    }
    const byKey = collectFields(value, known);
    const result = { ...value };
    for (const [key, plans] of byKey) {
      result[key] = convert(value[key], plans);
    }

    return result;
  };
  const result = convert(data, [plan]);
  if (mode === 'wire') {
    visitNumbers(result, token => {
      if (!classified.has(token)) {
        throw new Error(`Unclassified numeric GraphQL response token: ${token.value}`);
      }
    });
  }
  if (!isObject(result)) {
    throw new TypeError('GraphQL response data must be an object');
  }

  return result;
};

export const serializeVariables = ({
  decimal,
  operation,
  schema,
}: SerializeVariablesArgs): ApolloLink.Operation['variables'] => {
  const serialize = (value: unknown, type: GraphQLInputType): unknown => {
    if (value == null) {
      return value;
    }
    if (isNonNullType(type)) {
      return serialize(value, type.ofType);
    }
    if (isListType(type)) {
      return Array.isArray(value) ? value.map(item => serialize(item, type.ofType)) : value;
    }
    if (isScalarType(type) || isEnumType(type)) {
      return type.name === 'BigDecimal' ? decimal(value) : type.serialize(value);
    }
    if (!isInputObjectType(type) || !isObject(value)) {
      return value;
    }
    const fields = type.getFields();

    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, fields[key] ? serialize(item, fields[key].type) : item])
    );
  };
  const result = { ...operation.variables };
  const definition = getOperationAST(operation.query, operation.operationName);
  const variableDefinitions = definition?.variableDefinitions ?? [];
  for (const variable of variableDefinitions) {
    const key = variable.variable.name.value;
    const type = typeFromAST(schema, variable.type);
    if (type && isInputType(type) && Object.hasOwn(result, key)) {
      result[key] = serialize(result[key], type);
    }
  }

  return result;
};
